// src/pages/Home.tsx
import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import "./Home.css";
import { FaMobileAlt, FaWifi, FaStar, FaBullhorn, FaPaintBrush, FaGlobe, FaRobot, FaWindows, FaSatelliteDish } from "react-icons/fa";
import { FaBolt, FaPhoneAlt, FaLock, FaBriefcase } from "react-icons/fa";
import NbnSearchBar from "../components/NbnSearchBar.tsx";

// BLUE gradient options (simplified to two consistent deep-blue blends)
const SILVER_GRADIENTS = [
  "linear-gradient(135deg, #0b3f7a, #0f6ab4)",
  "linear-gradient(135deg, #0a427f, #0e62a8)"
];

// Alternate deep blues for variety (kept consistent)
const GREY_GRADIENTS = [
  "linear-gradient(135deg, #0a3f7d, #0c5d9e)",
  "linear-gradient(135deg, #0c437f, #0f66ad)"
];

// Small helper to choose one at random
const pickRandom = (arr: string[]) =>
  arr[Math.floor(Math.random() * arr.length)];

// Determine if gradient is light or dark
function getReadableTextColor(gradient: string): string {
  // Extract first colour in the gradient
  const match = gradient.match(/#([0-9a-fA-F]{6})/);
  if (!match) return "#000";

  const hex = match[0].replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Perceived brightness formula
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness > 150 ? "#111" : "#fff"; // lighter background ? dark text
}


const reviews = [
  {
    name: "Ashley D'Souza",
    role: "Business Manager",
    text: "Outstanding customer service. Superb follow up. Never letting a customer down.",
    rating: 5,
  },
  {
    name: "Matthew Brooks",
    role: "Senior Project Manager",
    text: "Alex spent countless hours, escalated faults, and kept downtime minimal during relocation.",
    rating: 5,
  },
  {
    name: "Samantha Lee",
    role: "IT Coordinator",
    text: "Jortel ensured smooth integration with our systems. Highly recommend their support.",
    rating: 4,
  },
  {
    name: "David Chen",
    role: "Operations Director",
    text: "Reliable and responsive. The static IP option was a game changer for our remote work.",
    rating: 5,
  },
];

// Business connectivity cards
const businessSolutions = [
  {
    tag: "HARDWARE & DEVICES",
    title: "Keep Up With Technology",
    blurb:
      "Hardware maintenance that covers your devices and entitles you to a brand new replacement every 5 years.",
    icon: <FaSatelliteDish />,
    bullets: [
      "Single bill & point of contact",
      "Optimised for hybrid teams",
      "Flexible as your business grows",
    ],
    link: "/plans/keep-up-with-technology",
  },
  {
    tag: "VOICE",
    title: "J-Voice Hosted",
    blurb: "Cloud-hosted phone system with enterprise call features and reliability.",
    icon: <FaSatelliteDish />,
    bullets: ["Auto-attendants & IVR", "Call queues & reporting", "Keep your existing numbers"],
    link: "/plans/hosted-voice",
  },
  {
    tag: "COLLABORATION",
    title: "J-Voice Microsoft Teams",
    blurb: "Microsoft Teams calling fully managed by Jortel for your workforce.",
    icon: <FaSatelliteDish />,
    bullets: [
      "Direct Routing with QoS",
      "Desk phone & softphone options",
      "Integrated presence & chat",
    ],
    link: "/plans/hosted-teams",
  },
  {
    tag: "INTERNET",
    title: "J-Data Fibre",
    blurb: "Premium, symmetric fibre for offices that can't afford to slow down.",
    icon: <FaSatelliteDish />,
    bullets: [
      "Static IP included options",
      "Business-grade uptime targets",
      "Scalable speeds as you grow",
    ],
    link: "/plans/fibre",
  },
];


const newsHighlights = [
  {
    title: "Jortel acquires Arid Land Communications",
    date: "Apr 2025",
    blurb: "Regional mobile and broadband expertise joins our nationwide network.",
    link: "/newsroom",
  },
  {
    title: "Applied VoIP now part of Jortel",
    date: "Nov 2023",
    blurb: "Deeper operational maturity to deliver consistent telecom experiences.",
    link: "/newsroom",
  },
  {
    title: "OzBill Solutions acquisition",
    date: "Jul 2023",
    blurb: "Expanded telecom capability and reach across Australia.",
    link: "/newsroom",
  },
  {
    title: "Diamond IT customer base acquired",
    date: "Aug 2022",
    blurb: "Seamless migration of active contracts with stronger national support.",
    link: "/newsroom",
  },
];

const industries = [
  {
    emoji: "🍽️",
    name: "Hospitality",
    text: "Reservation systems, Wi-Fi for guests and stable EFTPOS connectivity.",
  },
  {
    emoji: "🛍️",
    name: "Retail",
    text: "Fast, reliable links for POS, inventory and cloud apps in-store.",
  },
  {
    emoji: "🩺",
    name: "Healthcare",
    text: "Secure networks for clinics, telehealth and practice software.",
  },
  {
    emoji: "🎓",
    name: "Education",
    text: "Connectivity that keeps students, staff and campuses online.",
  },
  {
    emoji: "🏢",
    name: "Corporate Offices",
    text: "High-performance internet for hybrid teams, VoIP, and cloud operations.",
  },
  {
    emoji: "🏭",
    name: "Manufacturing",
    text: "Reliable networks for automation, sensors, logistics and warehouse systems.",
  },
  {
    emoji: "🚚",
    name: "Logistics & Transport",
    text: "Real-time tracking, fleet updates and warehouse Wi-Fi coverage.",
  },
  {
    emoji: "🏦",
    name: "Finance & Professional Services",
    text: "Secure connections for client platforms, remote staff and compliance needs.",
  },
  {
    emoji: "🏠",
    name: "Real Estate",
    text: "Stable connectivity for cloud CRM systems, VoIP and virtual inspections.",
  },
  {
    emoji: "💼",
    name: "Small Businesses",
    text: "Affordable, business-grade internet to support daily operations.",
  },
  {
    emoji: "🎪",
    name: "Events & Venues",
    text: "High-capacity temporary networks for ticketing, livestreaming and POS.",
  },
  {
    emoji: "💻",
    name: "IT & Tech Firms",
    text: "High-speed, low-latency internet for development, hosting and remote teams.",
  },
];


const reasons = [
  {
    icon: <FaBolt />,
    title: "Fast Setup",
    text: "Get connected quickly with zero hassle and clear communication.",
  },
  {
    icon: <FaPhoneAlt />,
    title: "Local Support",
    text: "Australian-based experts who know your environment and your team.",
  },
  {
    icon: <FaLock />,
    title: "Secure Networks",
    text: "Advanced protection and trusted partners to safeguard your data.",
  },
  {
    icon: <FaBriefcase />,
    title: "Business Specialists",
    text: "Solutions tailored to your industry, not one-size-fits-all bundles.",
  },
];

export default function Home(): JSX.Element {
  const fade = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
  const popupRef = useRef<HTMLDivElement | null>(null);

  // ? useEffect must go HERE
  useEffect(() => {
    const tiles = document.querySelectorAll(".ultra-premium");
    const popup = popupRef.current;

    tiles.forEach((tile: any) => {
      tile.addEventListener("mousemove", (e: MouseEvent) => {
        const rect = tile.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const rotateX = ((y - rect.height / 2) / 20) * -1;
        const rotateY = (x - rect.width / 2) / 20;

        tile.style.transform = `
          perspective(700px)
          rotateX(${rotateX}deg)
          rotateY(${rotateY}deg)
          translateY(-6px)
        `;
      });

      tile.addEventListener("mouseleave", () => {
        tile.style.transform = `
          perspective(700px)
          rotateX(0deg)
          rotateY(0deg)
          translateY(0)
        `;
      });
    });

    // popup after 2 seconds
    const timer = setTimeout(() => {
      if (popup) popup.classList.add("show");
    }, 2000);

    return () => {
      clearTimeout(timer);
      tiles.forEach((tile: any) => {
        tile.style.transform = "";
      });
      if (popup) popup.classList.remove("show");
    };
  }, []);

  // ?? **THIS MUST BE THE ONLY RETURN**
  return (
    <main className="home-page">
      {/* HERO SECTION */}
      <section className="hero">
        <video className="hero-video" autoPlay loop muted playsInline>
          <source src="/assets/iStock-1314570441.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay" />
        <div className="container hero-inner">
          <motion.h1 initial="hidden" animate="visible" variants={fade} className="hero-title">
            Seamless Communication,
            <br />
            Endless Possibilities
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fade}
            transition={{ delay: 0.12 }}
            className="hero-sub"
          >
            Accelerating Australia's digital future. One unified provider delivering advanced mobile, internet and business communications built for continuous growth.</motion.p>

          <div className="hero-cta">
            <Link className="home-btn primary" to="/plans">View Services</Link>
            <Link className="home-btn ghost" to="/contact">Contact Us</Link>
          </div>
        </div>
      </section>
      
{/* 1. BUSINESS CONNECTIVITY SOLUTIONS */}
<section className="business-solutions">
  <div className="container">
    <div className="big-signal-wrapper">
  <div className="big-signal">
    <div></div>
    <div></div>
    <div></div>
    <div></div>
  </div>
</div>

    <h2 className="business-title">Business Connectivity Solutions</h2>
    <p className="business-sub">
      Powerful, scalable and reliable services tailored to help Australian businesses grow,
      communicate and stay secure.
    </p>

    <div className="business-grid">
      {businessSolutions.map((item, i) => {
        const bg =
          i % 2 === 0
            ? pickRandom(SILVER_GRADIENTS)
            : pickRandom(GREY_GRADIENTS);

        const textColor = getReadableTextColor(bg);
        const subColor = textColor === "#111" ? "#444" : "#e8e8e8";

        return (
          <Link
            to={item.link}
            key={item.title}
            className="biz-card ultra-premium"
            style={{
              background: bg,
              color: textColor,
              border: "1px solid rgba(255,255,255,0.25)",
            }}
          >

            {/* Sparkles */}
            <span className="sparkle" style={{ top: "8%", left: "20%" }}></span>
            <span className="sparkle" style={{ top: "40%", left: "80%" }}></span>
            <span className="sparkle" style={{ top: "70%", left: "35%" }}></span>

            <div className="biz-icon" style={{ color: "#6f7acb" }}>
              {item.icon}
            </div>

            <div className="biz-tag" style={{ color: subColor }}>
              {item.tag}
            </div>

            <h3 style={{ color: textColor }}>{item.title}</h3>
              <p style={{ color: subColor }}>{item.blurb}</p>

            <ul className="biz-list" style={{ color: subColor }}>
              {item.bullets.map((b) => (
                <li key={b}>✓ {b}</li>
              ))}
            </ul>

            <span className="biz-link" style={{ color: textColor }}>
              Learn more 
            </span>
          </Link>
        );
      })}
    </div>
  </div>
</section>


      {/* 2. WHY BUSINESSES CHOOSE JORTEL */}
<section className="why-jortel">
  <div className="container">
    <h2 className="section-heading">Why Businesses Choose Jortel</h2>
    <p className="section-sub">
      We're more than a telco — businesses trust us because we combine
      enterprise-grade reliability with personalised support.
    </p>

    <div className="why-grid">
      {reasons.map((reason, i) => {
        const bg =
          i % 2 === 0
            ? pickRandom(SILVER_GRADIENTS)
            : pickRandom(GREY_GRADIENTS);

        const textColor = getReadableTextColor(bg);
        const subColor = textColor === "#111" ? "#333" : "#ececec";

        return (
          <div
            key={reason.title}
            className="why-item ultra-premium"
            style={{
              background: bg,
              color: textColor,
              border: "1px solid rgba(255,255,255,0.25)",
              position: "relative",
            }}
          >
            {/* ? SPARKLES */}
            <div className="sparkle" style={{ top: "10%", left: "20%" }}></div>
            <div className="sparkle" style={{ top: "70%", left: "80%" }}></div>
            <div className="sparkle" style={{ top: "50%", left: "40%" }}></div>

            <div className="why-emoji" style={{ color: textColor }}>
              {reason.icon}
            </div>

            <div>
              <h3 style={{ color: textColor }}>{reason.title}</h3>
              <p style={{ color: subColor }}>{reason.text}</p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
</section>


{/* 3. INDUSTRIES WE SERVE */}
<section className="industries">
  <div className="container">
    <h2 className="section-heading">Industries We Serve</h2>
    <p className="section-sub">
      From small businesses to national enterprises, Jortel delivers dependable connectivity
      across every major industry sector.
    </p>

    {/* VIEWPORT WRAPPER ? same structure as Reviews */}
    <div className="industries-viewport">
      <div className="industries-track">
        {[...industries, ...industries].map((ind, i) => {
          const bg =
            i % 2 === 0 ? pickRandom(SILVER_GRADIENTS) : pickRandom(GREY_GRADIENTS);

          const textColor = getReadableTextColor(bg);
          const subColor = textColor === "#111" ? "#444" : "#e8e8e8";

          return (
            <div
              key={`${ind.name}-${i}`}
              className="industry-card ultra-premium"
              style={{
                background: bg,
                color: textColor,
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              {/* Sparkles */}
              <span className="sparkle" style={{ top: "10%", left: "25%" }}></span>
              <span className="sparkle" style={{ top: "55%", left: "80%" }}></span>
              <span className="sparkle" style={{ top: "75%", left: "35%" }}></span>

              <div className="industry-emoji" style={{ fontSize: "30px" }}>
                {ind.emoji}
              </div>

              <h3>{ind.name}</h3>
              <p style={{ color: subColor }}>{ind.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  </div>
</section>


      {/* 4. OUR SOLUTIONS (ORIGINAL TILES) */}
      <section className="solutions">
        <div className="container">
          <h2 className="section-title">Our Solutions</h2>

          <div className="solutions-grid">
  {[
    {
      title: "Mobility Voice & Data",
      text: "SIM-only, banking, sharing, and roaming built for work and travel.",
      icon: <FaMobileAlt />,
      link: "/plans/mobile",
    },
    {
      title: "Business Grade Fast Internet",
      text: "NBN and fibre with static IP, low contention, and enterprise uptime.",
      icon: <FaWifi />,
      link: "/plans/nbn",
    },
    {
      title: "VoIP",
      text: "Cloud PBX and hosted voice with queues, IVRs, and call recording options.",
      icon: <FaPhoneAlt />,
      link: "/plans/hosted-voice",
    },
    {
      title: "Inbound",
      text: "13/1300/1800 numbers with flexible routing, per-second billing, and clear rates.",
      icon: <FaPhoneAlt />,
      link: "/plans/inbound-calls",
    },
    {
      title: "Microsoft Products",
      text: "Teams calling and Microsoft solutions integrated with your comms stack.",
      icon: <FaWindows />,
      link: "/plans/hosted-teams",
    },
    {
      title: "Web Design & Development",
      text: "Modern sites, refreshed branding, and campaign assets ready to launch.",
      icon: <FaPaintBrush />,
      link: "/plans/web-design",
    },
    {
      title: "Digital Marketing",
      text: "Strategy, calendars, creative, and reporting to grow your audience.",
      icon: <FaBullhorn />,
      link: "/plans/social-media-marketing",
    },
    {
      title: "Domain & Web Hosting",
      text: "Domains, secure hosting, and website care plans for your business.",
      icon: <FaGlobe />,
      link: "/contact",
    },
    {
      title: "AI (Coming Soon)",
      text: "Automation and smarter experiences powered by AI. Register your interest.",
      icon: <FaRobot />,
      link: "#",
    },
  ].map((card, i) => {
    const bg =
      i % 2 === 0 ? pickRandom(SILVER_GRADIENTS) : pickRandom(GREY_GRADIENTS);

    const textColor = getReadableTextColor(bg);
    const subColor = textColor === "#111" ? "#444" : "#ececec";

    return (
      <Link
        key={card.title}
        to={card.link}
        className="tile clickable-tile ultra-premium"
        style={{
          background: bg,
          color: textColor,
          border: "1px solid rgba(255,255,255,0.25)",
        }}
      >
        {/* ? Sparkles */}
        <span className="sparkle" style={{ top: "8%", left: "18%" }}></span>
        <span className="sparkle" style={{ top: "65%", left: "82%" }}></span>
        <span className="sparkle" style={{ top: "78%", left: "32%" }}></span>

        {/* Icon */}
        <div className="tile-icon" style={{ color: textColor }}>
          {card.icon}
        </div>

        <h3 style={{ color: textColor }}>{card.title}</h3>
        <p style={{ color: subColor }}>{card.text}</p>
      </Link>
    );
  })}
</div>

        </div>
      </section>

      



      {/* Newsroom Highlights */}
      <section className="newsroom-preview">
        <div className="container">
          <div className="newsroom-head">
            <h2 className="section-title">Latest from the Newsroom</h2>
            <Link to="/newsroom" className="newsroom-head__link">
              View all news
            </Link>
          </div>

          <div className="newsroom-grid">
            {newsHighlights.map((item) => (
              <Link to={item.link} className="newsroom-card" key={item.title}>
                <div className="newsroom-date">{item.date}</div>
                <h3>{item.title}</h3>
                <p>{item.blurb}</p>
                <span className="newsroom-read">Read more </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      {/* 6. CHECK NBN AVAILABILITY */}
      <section className="nbn-section">
        <div className="container">
          <NbnSearchBar />
        </div>
      </section>

      {/* 7. REVIEWS */}
          <section className="reviews">
      <div className="container">
        <h2 className="section-title">Customer Reviews</h2>
        <p className="muted">Trusted by our clients across Australia</p>

        <div className="reviews-viewport">
          <div className="reviews-track">
            {[...reviews, ...reviews].map((r, i) => (
              <div className="review-card" key={`${r.name}-${i}`}>
                
                {/* ?? Removed avatar, simplified header */}
                <div className="review-head no-avatar">
                  <div>
                    <div className="review-name">{r.name}</div>
                    <div className="review-role muted">{r.role}</div>
                  </div>
                </div>

                <div className="review-text">"{r.text}"</div>

                {/* ? Softer gold stars */}
                <div className="review-stars">
                  {Array.from({ length: r.rating }).map((_, k) => (
                    <FaStar key={k} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>


      {/* 8. CTA */}
      <section
        className="cta-band"
        style={{
          background: "linear-gradient(180deg, #16368fff, #16368fff)",
          color: "#ffffffff",
        }}
      >
        <div className="container cta-inner">
          <h3>Ready to Connect?</h3>
          <p>
            Call us at <strong>1300 567 835</strong> or get in touch today.
          </p>
          <Link
            to="/contact"
            className="btn ghost"
            style={{
              background: "#ffffffff",
              color: "#0a0a0a",
              border: "1px solid #d1d5db",
            }}
          >
            Contact Now
          </Link>
        </div>
      
      </section>
      <div
        id="promo-popup"
        className="promo-popup"
        ref={popupRef}
        style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 9999 }}
      >
        <div className="promo-text">
          Upgrade your business connectivity with Jortel!
        </div>

        <Link
          to="/contact"
          className="promo-btn"
          onClick={() => {
            if (popupRef.current) popupRef.current.classList.remove("show");
          }}
        >
          Contact Us
        </Link>

        <button
          className="promo-close"
          aria-label="Close"
          onClick={() => {
            if (popupRef.current) popupRef.current.classList.remove("show");
          }}
        >
          ×
        </button>
      </div>


    </main>
  );


}










